import { isProductionHost } from "consts";

export type ContentLinkKind =
  "problem" | "solution" | "card" | "comment" | "person";

export type ContentLinkTarget = {
  kind: ContentLinkKind;
  id: string | number;
  problemId?: string | number | null;
  solutionId?: string | number | null;
  cardId?: string | number | null;
};

export type ContentRoute = {
  pathname: string;
  hash: string;
};

const PROD_SHORT_LINK_BASE = "https://prsb.me/p";

// prsb.me — статический редиректор, единственная запись "p" в его links.json
// всегда указывает на продовый хост; в остальных окружениях ссылка "Поделиться"
// должна вести на резолвер текущего хоста, иначе она уведёт на продакшен.
const shortLinkBase = (): string =>
  isProductionHost ? PROD_SHORT_LINK_BASE : `${window.location.origin}/#/r`;

// Некоторые вызывающие компоненты передают "" вместо undefined/null для
// отсутствующего solutionId/cardId (напр. страница проблемы без решения) —
// сравнение с null такое не ловит, поэтому пустую строку тоже считаем "нет".
const present = (value?: string | number | null): value is string | number =>
  value !== undefined && value !== null && value !== "";

const ownerCardId = (target: ContentLinkTarget): string => {
  if (present(target.cardId)) return `attribute-${target.cardId}`;
  if (present(target.solutionId)) return `solution-${target.solutionId}`;

  return `problem-${target.problemId}`;
};

export const buildContentRoute = (target: ContentLinkTarget): ContentRoute => {
  if (target.kind === "person") {
    return { pathname: `/people/${target.id}`, hash: "" };
  }

  if (target.kind === "problem") {
    return { pathname: `/problems/${target.id}`, hash: "" };
  }

  if (target.kind === "solution") {
    return {
      pathname: `/problems/${target.problemId}/solutions/${target.id}`,
      hash: "",
    };
  }

  const pathname = present(target.solutionId)
    ? `/problems/${target.problemId}/solutions/${target.solutionId}`
    : `/problems/${target.problemId}`;

  if (target.kind === "card") {
    return { pathname, hash: `#attribute-${target.id}` };
  }

  return { pathname, hash: `#${ownerCardId(target)}~comment-${target.id}` };
};

export const buildShortLink = (target: ContentLinkTarget): string => {
  if (target.kind === "person") {
    return `${shortLinkBase()}/prs/${target.id}`;
  }

  if (target.kind === "problem") {
    return `${shortLinkBase()}/prb/${target.id}`;
  }

  const segments = ["prb", String(target.problemId)];

  if (target.kind === "solution") {
    // у самой solution-цели id решения — это target.id, а не target.solutionId
    // (solutionId в ContentLinkTarget означает "решение, которому принадлежит
    // карточка/комментарий", а не идентификатор самого решения)
    segments.push("sol", String(target.id));

    return `${shortLinkBase()}/${segments.join("/")}`;
  }

  if (present(target.solutionId)) {
    segments.push("sol", String(target.solutionId));
  }

  if (target.kind === "card") {
    segments.push("crd", String(target.id));

    return `${shortLinkBase()}/${segments.join("/")}`;
  }

  if (present(target.cardId)) {
    segments.push("crd", String(target.cardId));
  }

  segments.push("cmt", String(target.id));

  return `${shortLinkBase()}/${segments.join("/")}`;
};

/**
 * Разбирает цепочку tag/id пар из /r/... в
 * канонический маршрут приложения. Работает полностью на клиенте — вся
 * цепочка предков уже закодирована в самом пути, запросов к бэкенду не нужно.
 */
export const parseShortLinkPath = (segments: string[]): ContentRoute | null => {
  if (segments.length === 0 || segments.length % 2 !== 0) return null;

  const pairs: Array<[string, string]> = [];

  for (let i = 0; i < segments.length; i += 2) {
    const id = segments[i + 1];

    if (!/^\d+$/.test(id)) return null;

    pairs.push([segments[i], id]);
  }

  const [firstTag, firstId] = pairs[0];

  if (firstTag === "prs") {
    return pairs.length === 1
      ? buildContentRoute({ kind: "person", id: firstId })
      : null;
  }

  if (firstTag !== "prb") return null;

  const problemId = firstId;
  let rest = pairs.slice(1);

  if (rest.length === 0) {
    return buildContentRoute({ kind: "problem", id: problemId });
  }

  let solutionId: string | undefined;

  if (rest[0][0] === "sol") {
    solutionId = rest[0][1];
    rest = rest.slice(1);
  }

  if (rest.length === 0) {
    if (solutionId == null) return null;

    return buildContentRoute({ kind: "solution", id: solutionId, problemId });
  }

  let cardId: string | undefined;

  if (rest[0][0] === "crd") {
    cardId = rest[0][1];
    rest = rest.slice(1);

    if (rest.length === 0) {
      return buildContentRoute({
        kind: "card",
        id: cardId,
        problemId,
        solutionId,
      });
    }
  }

  if (rest.length === 1 && rest[0][0] === "cmt") {
    return buildContentRoute({
      kind: "comment",
      id: rest[0][1],
      problemId,
      solutionId,
      cardId,
    });
  }

  return null;
};
